import anime from 'animejs';
import lottie from 'lottie-web';

document.addEventListener('DOMContentLoaded', () => {
	const explainer = document.querySelector<HTMLElement>('[data-explainer]');
	const item = explainer?.querySelector<HTMLElement>('[data-explainer-item]');
	const photo = item?.querySelector<HTMLElement>('[data-photo]');
	const transition = item?.querySelector<HTMLElement>('[data-transition]');
	const illustration = item?.querySelector<HTMLElement>('[data-illustration]');
	const words = explainer?.querySelector<HTMLElement>('[data-words]');
	const usage = explainer?.querySelector<HTMLElement>('[data-usage]');

	if (
		!(
			explainer &&
			item &&
			photo &&
			transition &&
			illustration &&
			words &&
			usage
		)
	) {
		return;
	}

	illustration.style.opacity = '0';

	let usageRect = usage.getBoundingClientRect();
	window.addEventListener('resize', () => {
		usageRect = usage.getBoundingClientRect();
	});

	/**
	 * Scroll animation timeline
	 */

	const scrollTl = anime.timeline({
		autoplay: false,
		duration: 500,
	});

	let itemProps = {
		pos: 0,
		itemRotate: 0,
		itemScale: 1,
		itemOpacity: 1,
	};

	const updateItem = () => {
		const x = (30 + window.innerHeight * 0.02) * itemProps.pos;
		const y = (30 + window.innerHeight * 0.3) * itemProps.pos;
		item.style.transform = `translate3d(${x}px, ${y}px, 0) rotate(${itemProps.itemRotate}deg) scale(${itemProps.itemScale})`;
		item.style.opacity = itemProps.itemOpacity.toString();
	};

	window.addEventListener('resize', updateItem);

	scrollTl.add({
		targets: itemProps,
		update: updateItem,
		pos: 0.33,
		itemOpacity: 1,
		itemScale: 1.2,
		itemRotate: 25,
		easing: 'easeInQuad',
	});

	scrollTl.add({
		targets: itemProps,
		update: updateItem,
		pos: 0.66,
		itemScale: 0.55,
		itemRotate: 40,
		easing: 'easeOutQuad',
	});

	scrollTl.add({
		targets: itemProps,
		update: updateItem,
		pos: 1,
		itemOpacity: [1, 0],
		itemScale: 0.36,
		itemRotate: 40,
		easing: 'linear',
	});

	scrollTl.add(
		{
			targets: photo,
			opacity: [1, 0],
			duration: 50,
			easing: 'linear',
		},
		300,
	);

	scrollTl.add(
		{
			targets: transition,
			opacity: [0, 1],
			duration: 50,
			easing: 'linear',
		},
		280,
	);

	scrollTl.add(
		{
			targets: transition,
			opacity: [1, 0],
			duration: 50,
			easing: 'linear',
		},
		500,
	);

	scrollTl.add(
		{
			targets: illustration,
			opacity: [0, 1],
			duration: 50,
			easing: 'linear',
		},
		500,
	);

	/**
	 *
	 */
	const usageAnimation = lottie.loadAnimation({
		container: usage,
		autoplay: false,
		loop: false,
		path: window.otStrings.usageAnimation,
	});

	/**
	 * Drop, fill, shake animation timeline
	 */
	const dropFillShakeAnimation = makeWordsAnimation(words);

	/**
	 * Hook up to scroll
	 */
	let didPlayAnimation = false;
	let nextFrame: number | undefined;
	window.addEventListener('scroll', () => {
		if (nextFrame) {
			cancelAnimationFrame(nextFrame);
		}
		nextFrame = requestAnimationFrame(() => {
			const pos = window.scrollY / (window.innerHeight * 0.6);
			const clamped = Math.max(0, Math.min(pos, 1));

			scrollTl.seek(scrollTl.duration * clamped);

			if (didPlayAnimation == false && itemProps.pos > 0.95) {
				anime({
					targets: item,
					opacity: 0,
					easing: 'linear',
					duration: 300,
					complete: () => {
						item.style.display = 'none';
					},
				});
				dropFillShakeAnimation.play();
				usageAnimation.play();
				didPlayAnimation = true;
			}

			if (didPlayAnimation == true && itemProps.pos < 0.2) {
				item.style.display = 'block';
				dropFillShakeAnimation.pause();
				dropFillShakeAnimation.seek(0);
				usageAnimation.stop();
				didPlayAnimation = false;
			}
		});
	});
});

const makeWordsAnimation = (element: HTMLElement) => {
	const duration = 1000;
	const delay = 1250;

	const dfsTl = anime.timeline({
		autoplay: false,
		duration: 0,
		delay: 0,
		easing: 'cubicBezier(.95,0,.15,1.32)',
	});

	const words = Array.from(element.querySelectorAll('span') ?? []);
	words.forEach((word) => (word.style.fontSize = '26px'));

	// Drop
	dfsTl.add({
		targets: words[0],
		keyframes: [
			{
				duration: 0,
				fontSize: 26,
			},
			{
				duration,
				fontSize: window.innerWidth > 768 ? 72 : 52,
			},
			{
				delay,
				duration,
				fontSize: 26,
			},
		],
	});

	// Fill
	dfsTl.add(
		{
			targets: words[1],
			keyframes: [
				{
					duration: 0,
					fontSize: 26,
				},
				{
					duration,
					fontSize: window.innerWidth > 768 ? 72 : 52,
				},
				{
					delay,
					duration,
					fontSize: 26,
				},
			],
		},
		duration + delay,
	);

	// Shake
	dfsTl.add(
		{
			targets: words[2],
			keyframes: [
				{
					duration: 0,
					fontSize: 26,
				},
				{
					duration,
					fontSize: window.innerWidth > 768 ? 72 : 52,
				},
				{
					delay,
					duration,
					fontSize: 26,
				},
			],
		},
		(duration + delay) * 2,
	);

	return dfsTl;
};
